/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

const TEST_PLUGIN_RELOAD_QUERY_PARAM = 'gw_reload';

const normalizeRevisionToken = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  // Strip weak etag prefix and wrapping quotes before encoding.
  const withoutWeakPrefix = trimmedValue.replace(/^W\//, '');
  const normalizedValue = withoutWeakPrefix.replace(/^"|"$/g, '');

  return normalizedValue ? encodeURIComponent(normalizedValue) : null;
};

const getRevisionTokenFromHeaders = (headers: Headers): string | null => {
  const etag = headers.get('etag');
  if (etag) {
    return normalizeRevisionToken(etag);
  }

  const lastModified = headers.get('last-modified');
  if (lastModified) {
    return normalizeRevisionToken(lastModified);
  }

  return null;
};

const hashContent = (value: string): string => {
  const modulus = 2_147_483_647;
  let hash = 7;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % modulus;
  }

  return `content-${hash.toString(16)}`;
};

const resolvePluginRevisionToken = async (
  pluginUrl: string,
): Promise<string | undefined> => {
  try {
    const getResponse = await fetch(pluginUrl, {
      cache: 'no-store',
    });
    if (getResponse.ok) {
      const tokenFromHeaders = getRevisionTokenFromHeaders(getResponse.headers);
      if (tokenFromHeaders) {
        return tokenFromHeaders;
      }

      // CORS often hides ETag and some static servers omit Last-Modified.
      // Fall back to hashing bundle content so URL changes follow code changes.
      const content = await getResponse.text();
      if (content) {
        return hashContent(content);
      }
    }
  } catch {
    // Ignore revision lookup failures and continue without cache busting.
  }

  return undefined;
};

const appendRevisionToUrl = (url: string, revisionToken: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${TEST_PLUGIN_RELOAD_QUERY_PARAM}=${revisionToken}`;
};

export const resolveLiveReloadPluginUrl = async (
  pluginUrl: string,
): Promise<string> => {
  const revisionToken = await resolvePluginRevisionToken(pluginUrl);
  return revisionToken
    ? appendRevisionToUrl(pluginUrl, revisionToken)
    : pluginUrl;
};
